import { useState, useRef, useEffect } from 'react';

interface UserAvatarProps {
  src?: string | null;
  alt?: string;
  /** Tailwind size classes, e.g. "h-10 w-10". Defaults to "h-10 w-10". */
  size?: string;
  /** Initials to show when image is missing or fails to load. */
  initials: string;
  /** Extra classes applied to the container (e.g. "shrink-0 border-2"). */
  className?: string;
  /** Tailwind text size for initials. Defaults to "text-sm". */
  textSize?: string;
  /** Fallback background classes. Defaults to blue gradient. */
  fallbackBg?: string;
  /** Fallback text color. Defaults to "font-medium text-white". */
  fallbackText?: string;
}

export function UserAvatar({
  src,
  alt = '',
  size = 'h-10 w-10',
  initials,
  className = '',
  textSize = 'text-sm',
  fallbackBg = 'bg-gradient-to-br from-blue-400 to-blue-600',
  fallbackText = 'font-medium text-white',
}: UserAvatarProps) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [cacheBust, setCacheBust] = useState('');
  const retryCount = useRef(0);

  // Reset state when src changes
  useEffect(() => {
    setImgLoaded(false);
    setImgError(false);
    setCacheBust('');
    retryCount.current = 0;
  }, [src]);

  const showImg = !!src && !imgError;
  const imgSrc =
    src && cacheBust ? `${src}${src.includes('?') ? '&' : '?'}t=${cacheBust}` : (src ?? undefined);

  return (
    <div
      className={`relative flex ${size} shrink-0 items-center justify-center overflow-hidden rounded-full ${imgLoaded ? '' : fallbackBg} ${className}`}
    >
      {/* Initials — always rendered, visible until a real image loads */}
      {!imgLoaded && <span className={`${textSize} ${fallbackText}`}>{initials}</span>}

      {/* Image overlay — hidden until onLoad confirms a real (non-tiny) image */}
      {showImg && (
        <img
          src={imgSrc}
          alt={alt}
          className={`absolute inset-0 h-full w-full rounded-full object-cover transition-opacity duration-200 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={(e) => {
            const img = e.currentTarget;
            // Server returns a 1x1 transparent GIF for missing avatars —
            // treat tiny images as "no avatar"
            if (img.naturalWidth > 1 && img.naturalHeight > 1) {
              setImgLoaded(true);
              retryCount.current = 0;
            } else {
              setImgError(true);
            }
          }}
          onError={() => {
            if (retryCount.current < 1) {
              retryCount.current += 1;
              setCacheBust(Date.now().toString());
            } else {
              setImgError(true);
            }
          }}
        />
      )}
    </div>
  );
}
